import React, { useEffect, useState } from "react";
import { getStoredApiKeys } from "../../services/discogs";
import { setDoc, doc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase/config";
import { Save, Key, Eye, EyeOff, Check, Loader2, ExternalLink, Music2 } from "lucide-react";

export default function AdminVaultSettings() {
  const [keys, setKeys] = useState({
    discogs_token: "",
    gemini_api_key: "",
    spotify_client_id: "",
    spotify_client_secret: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [show, setShow] = useState({ discogs: false, gemini: false, spo_id: false, spo_sec: false });
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    getStoredApiKeys().then((k) => {
      setKeys({
        discogs_token: k.discogs_token || "",
        gemini_api_key: k.gemini_api_key || "",
        spotify_client_id: k.spotify_client_id || "",
        spotify_client_secret: k.spotify_client_secret || "",
      });
      setLoading(false);
    });
  }, []);

  const save = async () => {
    setSaving(true);
    setErr("");
    setSaved(false);
    try {
      await setDoc(doc(db, "settings", "api_keys"), {
        discogs_token: (keys.discogs_token || "").trim(),
        gemini_api_key: (keys.gemini_api_key || "").trim(),
        spotify_client_id: (keys.spotify_client_id || "").trim(),
        spotify_client_secret: (keys.spotify_client_secret || "").trim(),
        updated_at: serverTimestamp(),
      }, { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setErr(e.message || "Yadda saxlanmadı");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm text-[var(--text-muted)]">Yüklənir…</div>;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="vault-settings">
      {/* Discogs */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Key size={14} className="text-[var(--accent)]" />
          <span className="text-sm font-medium">Discogs Personal Token</span>
        </div>
        <SecretInput
          value={keys.discogs_token}
          onChange={(v) => setKeys((s) => ({ ...s, discogs_token: v }))}
          show={show.discogs}
          onToggle={() => setShow((s) => ({ ...s, discogs: !s.discogs }))}
          placeholder="Discogs API token"
          testId="vault-discogs-token"
        />
        <div className="text-[11px] text-[var(--text-muted)] mt-1">
          Barkod axışı üçün.
          <a href="https://www.discogs.com/settings/developers" target="_blank" rel="noreferrer" className="text-[var(--accent)] inline-flex items-center gap-1 ml-2">
            Discogs Developer <ExternalLink size={10} />
          </a>
        </div>
      </div>

      {/* Gemini */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Key size={14} className="text-[#4285F4]" />
          <span className="text-sm font-medium">Gemini API Key (Google AI)</span>
        </div>
        <SecretInput
          value={keys.gemini_api_key}
          onChange={(v) => setKeys((s) => ({ ...s, gemini_api_key: v }))}
          show={show.gemini}
          onToggle={() => setShow((s) => ({ ...s, gemini: !s.gemini }))}
          placeholder="Gemini API key"
          testId="vault-gemini-key"
        />
        <div className="text-[11px] text-[var(--text-muted)] mt-1">
          AI zənginləşdirmə (AZ/EN/RU təsvir + Spotify link təxmini) üçün.
          <a href="https://aistudio.google.com/apikey" target="_blank" rel="noreferrer" className="text-[var(--accent)] inline-flex items-center gap-1 ml-2">
            Google AI Studio <ExternalLink size={10} />
          </a>
        </div>
      </div>

      {/* Spotify Client Credentials */}
      <div className="border border-[#1DB954]/30 bg-[#1DB954]/5 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Music2 size={14} className="text-[#1DB954]" />
          <span className="text-sm font-medium">Spotify Developer Credentials</span>
          <span className="text-[9px] tracking-[0.14em] text-[#1DB954] bg-[#1DB954]/15 px-1.5 py-0.5">CANLI AXTARIŞ</span>
        </div>
        <div className="space-y-2">
          <div>
            <label className="text-[10px] tracking-[0.14em] text-[var(--text-muted)] font-medium block mb-1">CLIENT ID</label>
            <SecretInput
              value={keys.spotify_client_id}
              onChange={(v) => setKeys((s) => ({ ...s, spotify_client_id: v }))}
              show={show.spo_id}
              onToggle={() => setShow((s) => ({ ...s, spo_id: !s.spo_id }))}
              placeholder="32-simvol client ID"
              testId="vault-spotify-cid"
            />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.14em] text-[var(--text-muted)] font-medium block mb-1">CLIENT SECRET</label>
            <SecretInput
              value={keys.spotify_client_secret}
              onChange={(v) => setKeys((s) => ({ ...s, spotify_client_secret: v }))}
              show={show.spo_sec}
              onToggle={() => setShow((s) => ({ ...s, spo_sec: !s.spo_sec }))}
              placeholder="32-simvol client secret"
              testId="vault-spotify-secret"
            />
          </div>
        </div>
        <div className="text-[11px] text-[var(--text-muted)] mt-2 leading-relaxed">
          Real vaxt Spotify albom axtarışı üçün (AI-nin hallüsinasiyaları olmadan).
          <a href="https://developer.spotify.com/dashboard" target="_blank" rel="noreferrer" className="text-[#1DB954] inline-flex items-center gap-1 ml-1">
            Spotify Developer <ExternalLink size={10} />
          </a>
          <div className="mt-1">Yeni app yarat → yalnız <b>Web API</b> seçin → Client ID + View Client Secret.</div>
        </div>
      </div>

      {err && <div className="text-sm text-[var(--accent)]">{err}</div>}

      <div className="flex items-center gap-3 pt-2 border-t border-[var(--border)]">
        <button onClick={save} disabled={saving} className="btn-primary" style={{ textTransform: "none", letterSpacing: "0" }} data-testid="vault-save-keys">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Yadda saxla
        </button>
        {saved && (
          <span className="text-sm text-green-500 flex items-center gap-1" data-testid="vault-saved">
            <Check size={14} /> Yadda saxlandı
          </span>
        )}
      </div>
    </div>
  );
}

function SecretInput({ value, onChange, show, onToggle, placeholder, testId }) {
  return (
    <div className="relative">
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="input pr-10"
        data-testid={testId}
        autoComplete="off"
      />
      <button
        type="button"
        onClick={onToggle}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]"
        tabIndex={-1}
      >
        {show ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>
    </div>
  );
}
